import { Alert, AlertTitle, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { Karta } from '@/components/Karta';
import { PasSekcji } from '@/components/PasSekcji';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { DataQualityBadge, EmptyState, ErrorState, LoadingState } from '@/components/states';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import type { Dowod } from '@/components/SzufladaDowodu';
import { BrakDostepuDoDostaw } from './BrakDostepuDoDostaw';
import { czyWidziDostawy } from '@/lib/auth/dostepZrodel';
import { MIN_DELIVERIES, PROBLEM_THRESHOLD_PCT, problemSchoolsFrom } from './problemSchools';
import {
  KartaMetryki,
  KLASYFIKACJA_DRIVE,
  OKNO_TOLERANCJI,
  PasekTrzechWynikow,
  PaskKlasyfikacjiZrodla,
  SWIEZOSC_DRIVE,
  ZRODLO_COVERAGE,
  ZRODLO_SUMMARY,
  coSieNieLiczy,
  formatMinuty,
  formatPct,
  formatPunkty,
  trojpodzial,
  udzial,
} from './punktualnosc';
import { useAuth } from '@/lib/auth/AuthContext';
import { opiszPokrycie, policzPokrycie } from '@/lib/pokrycieSerii';
import { formatCount, formatRange } from '@/lib/format';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { useDeliveryBySchool, useDeliverySummary, useDeliveryTrend, useTuszamaIdCoverage } from '@/lib/useAnalytics';
import type { DeliveryTrendPoint } from '@/lib/api/drive';

/**
 * PRZEGLĄD DOSTAW — migracja do języka „SYGNAŁ".
 *
 * Pytanie ekranu: CZY DOSTAWY TRAFIAJĄ W GODZINĘ, KTÓRĄ SZKOŁA MIAŁA ZAPISANĄ.
 *
 * Zmienia się hierarchia i zmienia się jedna rzecz merytoryczna: punktualność przestaje być
 * pytaniem tak/nie. Ma TRZY wyniki — na czas, przed czasem, spóźnione — i drugi z nich nie jest
 * trafieniem. Poprzedni układ czterech równorzędnych kafelków chował „przed czasem" w przypisie
 * pod liczbą dostaw; tam nikt go nie czytał, a to jest osobny defekt operacyjny.
 *
 * ŻADNYCH DANYCH DEMONSTRACYJNYCH. Ekran czyta wyłącznie API Drive przez istniejące haki.
 * Puste okno pokazujemy jako pustkę, nie jako zero.
 */

const KIERUNEK_TRENDU: Record<string, string> = {
  up: 'rosnący',
  down: 'malejący',
  flat: 'płaski',
  unknown: 'nieustalony',
};

/**
 * Punktualność dzień po dniu. Seria jest pobierana od początku (useDeliveryTrend), ale poprzedni
 * układ używał z niej WYŁĄCZNIE pola `direction` i wyrzucał resztę — dokładnie ten sam defekt,
 * który analiza parytetu wykryła przy dziennej serii sprzedaży.
 *
 * Dzień bez dostaw NIE jest dniem z zerową punktualnością. Zero to zmierzony wynik, brak dostaw
 * to brak pomiaru — dlatego mają dwie różne formy, a nie jedną wysokość słupka.
 */
function PasekDni({
  punkty,
  zakres,
}: {
  punkty: readonly DeliveryTrendPoint[];
  zakres: { date_from: string; date_to: string } | null;
}) {
  if (punkty.length === 0) return null;

  const pierwszy = punkty[0];
  const ostatni = punkty[punkty.length - 1];
  // Pokrycie liczy osobno OKNO i SERIĘ. Wcześniej podpis brał długość serii za długość okna,
  // przez co roczne zapytanie z 42 dniami dostaw czytało się jako „okno miało 42 dni”.
  const pokrycie = policzPokrycie(zakres, punkty);

  return (
    <Paper variant="outlined" data-cc-card-id="DRIVE-14" sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'baseline', flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="h6">Punktualność dzień po dniu</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" data-cc-card-id="DRIVE-15" sx={{ color: 'var(--tekst-slaby)' }}>
          {opiszPokrycie(pokrycie)}
        </Typography>
      </Stack>

      <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mb: 2, maxWidth: '80ch' }}>
        Wysokość słupka to udział rekordów na czas w danym dniu. Słupek poniżej progu{' '}
        {PROBLEM_THRESHOLD_PCT}% ma barwę uwagi. Dzień obecny w serii z zerem dostaw jest jasną kolumną —
        to brak pomiaru, a nie 0%.{' '}
        {pokrycie.dniNieobecne !== null && pokrycie.dniNieobecne > 0
          ? `Dni, w których nie było ani jednej dostawy, źródło pomija — nie ma ich na wykresie, więc sąsiadujące słupki mogą dzielić więcej niż jedna doba.`
          : ''}
      </Typography>

      <Box
        role="img"
        aria-label={`Udział dostaw na czas dzień po dniu. ${opiszPokrycie(pokrycie)}.`}
        sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 120, mb: 1 }}
      >
        {punkty.map((p) => {
          const pct = p.deliveries > 0 ? p.on_time_pct : null;
          if (pct === null) {
            return (
              <Box
                key={p.date}
                title={`${p.date}: brak dostaw w tym dniu — brak pomiaru, nie 0%`}
                sx={{
                  flex: 1,
                  minWidth: 2,
                  height: '100%',
                  background: 'var(--tlo-glebokie)',
                  borderRadius: 'var(--r-drobny) var(--r-drobny) 0 0',
                }}
              />
            );
          }
          return (
            <Box
              key={p.date}
              title={`${p.date}: ${formatPct(pct)} na czas · ${formatCount(p.deliveries)} dostaw · przed czasem ${formatCount(
                p.early,
              )} · spóźnione ${formatCount(p.late)}`}
              sx={{
                flex: 1,
                minWidth: 2,
                height: `${Math.max(pct, 2)}%`,
                background: pct >= PROBLEM_THRESHOLD_PCT ? 'var(--sukces)' : 'var(--uwaga)',
                borderRadius: 'var(--r-drobny) var(--r-drobny) 0 0',
              }}
            />
          );
        })}
      </Box>

      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
          {pierwszy ? pierwszy.date : '—'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
          {ostatni ? ostatni.date : '—'}
        </Typography>
      </Stack>
    </Paper>
  );
}

export function DriveOverviewPage() {
  const auth = useAuth();
  const summary = useDeliverySummary();
  const bySchool = useDeliveryBySchool();
  const trend = useDeliveryTrend();
  const coverage = useTuszamaIdCoverage();

  if (!czyWidziDostawy(auth)) return <BrakDostepuDoDostaw />;
  if (summary.isLoading) return <LoadingState label="Wczytywanie danych o dostawach…" />;
  if (summary.error) {
    return (
      <ErrorState
        error={summary.error}
        context="Control Center czyta wyłącznie API Drive. Gdy backend nie odpowiada albo sesja wygasła, nie pokazujemy liczb zastępczych."
      />
    );
  }
  if (!summary.data) return <EmptyState title="Brak danych o dostawach" />;

  const { current, previous, range } = summary.data;
  const windowLabel = formatRange(range.date_from, range.date_to);
  const porownanie = formatRange(range.comparison.date_from, range.comparison.date_to);
  const wyniki = trojpodzial(current);
  const problems = problemSchoolsFrom(bySchool.data?.schools ?? []);

  /*
   * ZERO TO NIE TO SAMO CO PUSTO (reguła i testy: lib/pustyZbior.ts).
   * Dla dostaw rozstrzyga liczba REKORDÓW w oknie: gdy jest ich zero, nie ma czego liczyć i żadna
   * liczba pochodna nie jest pomiarem. czyPustyZbior() dokłada drugi warunek — brak szkół w
   * rozbiciu — dzięki czemu układ „zero dostaw, a szkoły w rozbiciu są" nie ginie po cichu,
   * tylko dostaje własną diagnozę.
   */
  const szkolWWyniku = bySchool.data?.schools.length ?? null;
  const ziarno = { wierszy: current.deliveries, bytow: szkolWWyniku };
  const brakRekordow = current.deliveries === 0;
  const pusteZrodlo = czyPustyZbior(ziarno);
  const rozjazdZiarna = brakRekordow && szkolWWyniku !== null && szkolWWyniku > 0;

  const liczbaNaEkran = (v: number | null): string | null => {
    const w = brakRekordow ? null : wartoscDoPokazania(v, ziarno);
    return w === null ? null : formatCount(w);
  };

  const naCzasPct = brakRekordow ? null : wartoscDoPokazania(current.on_time_pct, ziarno);
  const kontrolaPct = udzial(current.on_time, current.deliveries);
  const rozbieznoscKontroli =
    kontrolaPct !== null && current.on_time_pct !== null && Math.abs(kontrolaPct - current.on_time_pct) > 0.5;

  /*
   * Dowód budowany z TYCH SAMYCH pól, które zasilają liczbę na ekranie. Warstwa prezentacji nie
   * jest właścicielem liczb — jest natomiast ostatnim miejscem, w którym da się złapać sprzeczność
   * między liczbą podaną przez źródło a jej własnymi licznikami. Stąd wiersz „kontrola".
   */
  const dowodPunktualnosci: Dowod = {
    tytul: 'Udział dostaw na czas',
    etykietaWartosci: 'Udział rekordów w oknie',
    wartosc: naCzasPct === null ? null : formatPct(naCzasPct),
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: ZRODLO_SUMMARY,
    klasyfikacja: KLASYFIKACJA_DRIVE,
    swiezosc: SWIEZOSC_DRIVE,
    oknoBiezace: windowLabel,
    oknoPorownawcze: porownanie,
    wyliczenie:
      'Udział podaje źródło (pole on_time_pct). Control Center liczy do niego niezależną kontrolę: ' +
      'rekordy ze statusem „na czas" ÷ wszystkie rekordy dostaw w oknie.',
    szczegoly: [
      ['Rekordy dostaw w oknie', formatCount(current.deliveries)],
      ['Na czas', `${formatCount(wyniki.naCzas)} · ${formatPct(udzial(wyniki.naCzas, wyniki.dostawy))}`],
      ['Przed czasem', `${formatCount(wyniki.przedCzasem)} · ${formatPct(udzial(wyniki.przedCzasem, wyniki.dostawy))}`],
      ['Spóźnione', `${formatCount(wyniki.spoznione)} · ${formatPct(udzial(wyniki.spoznione, wyniki.dostawy))}`],
      [
        'Spóźnienia w kubełkach',
        `do 5 min ${formatCount(current.late_5)} · do 15 min ${formatCount(current.late_15)} · ponad 15 min ${formatCount(
          current.late_over_15,
        )}`,
      ],
      ['Bez klasyfikacji', formatCount(wyniki.nieprzypisane)],
      ['Udział podany przez źródło', formatPct(current.on_time_pct)],
      ['Kontrola: na czas ÷ rekordy', formatPct(kontrolaPct)],
      ['Okno porównawcze — na czas', formatPct(previous.on_time_pct)],
      ['Zmiana', formatPunkty(summary.data.on_time_delta_points)],
    ],
    proba: `${formatCount(current.deliveries)} rekordów dostaw`,
    jakoscDanych: current.data_quality_reason,
    ograniczenia: [
      OKNO_TOLERANCJI,
      ...coSieNieLiczy(current),
      ...(pusteZrodlo
        ? [
            'ŹRÓDŁO ZWRÓCIŁO PUSTY ZBIÓR: zero rekordów dostaw i zero szkół w rozbiciu w tym oknie. ' +
              'To NIE jest zmierzona punktualność zero — nie ma czego liczyć.',
          ]
        : []),
      ...(wyniki.rozjazd
        ? [
            `Trzy liczniki (na czas ${formatCount(wyniki.naCzas)}, przed czasem ${formatCount(
              wyniki.przedCzasem,
            )}, spóźnione ${formatCount(wyniki.spoznione)}) nie sumują się do liczby rekordów w oknie ` +
              `(${formatCount(current.deliveries)}). Różnica jest pokazana osobno jako „bez klasyfikacji" i NIE jest ` +
              'doliczana do żadnego z trzech wyników.',
          ]
        : []),
      ...(rozbieznoscKontroli
        ? [
            `Udział podany przez źródło (${formatPct(current.on_time_pct)}) różni się od kontroli policzonej z ` +
              `liczników (${formatPct(kontrolaPct)}). Oznacza to, że źródło liczy udział na innym mianowniku niż ` +
              'wszystkie rekordy w oknie. Na ekranie pokazana jest liczba źródła, a nie kontrola.',
          ]
        : []),
    ],
    czegoNieWynika:
      'Z tego udziału nie wynika, że posiłek trafił do ucznia we właściwym momencie: przyjazd PRZED czasem jest ' +
      'tu osobnym wynikiem, a nie trafieniem — dostawa przed otwarciem stołówki potrafi znaczyć, że jedzenie ' +
      'stało do przerwy. Nie wynika z niego również przyczyna: /delivery-summary jest agregatem okna i nie ' +
      'zawiera ani kierowcy, ani trasy, ani szkoły, więc na tej liczbie nie da się wskazać, gdzie leży problem.',
  };

  const dowodOdchylenia: Dowod = {
    tytul: 'Średnie odchylenie od planowanej godziny',
    etykietaWartosci: 'Średnia z rekordów z zapisanym odchyleniem',
    wartosc: brakRekordow ? null : formatMinuty(current.avg_delta_min),
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: 'Drive /analytics/delivery-summary → deliveries.delta_min',
    klasyfikacja: KLASYFIKACJA_DRIVE,
    swiezosc: SWIEZOSC_DRIVE,
    oknoBiezace: windowLabel,
    oknoPorownawcze: porownanie,
    wyliczenie: 'Średnia odchyleń (godzina faktyczna − planowana) po rekordach, które mają zapisane odchylenie.',
    szczegoly: [
      ['Mediana', formatMinuty(current.median_delta_min) ?? 'brak danych'],
      ['Zmiana wobec okna porównawczego', formatMinuty(summary.data.avg_delta_change_min) ?? 'brak danych'],
      ['Rekordy bez zapisanego odchylenia', formatCount(current.without_delta)],
      ['Rekordy dostaw w oknie', formatCount(current.deliveries)],
    ],
    proba: `${formatCount(Math.max(current.deliveries - current.without_delta, 0))} rekordów z odchyleniem`,
    jakoscDanych: current.data_quality_reason,
    ograniczenia: [
      'Odpowiedź nie mówi, czy do średniej wchodzą także odchylenia UJEMNE, czyli przyjazdy przed czasem. ' +
        'Jeśli wchodzą, wcześniejszy przyjazd skraca średnią, mimo że nie jest trafieniem.',
      ...coSieNieLiczy(current),
    ],
    czegoNieWynika:
      'Średnia nie mówi, ile dostaw było spóźnionych: kilka rekordów spóźnionych bardzo daje ten sam wynik co ' +
      'wiele spóźnionych lekko. Mediana obok jest odporniejsza na skrajności, ale żadna z tych dwóch liczb nie ' +
      'pokazuje rozrzutu — a to on decyduje o tym, czy dzień był przewidywalny dla szkoły.',
  };

  const pokrycie = coverage.data?.deliveries;
  const dowodZlaczenia: Dowod = {
    tytul: 'Dostawy gotowe do łączenia ze sprzedażą',
    etykietaWartosci: 'Udział rekordów z obydwoma identyfikatorami',
    wartosc: pokrycie ? formatPct(pokrycie.joinable_pct) : null,
    zrodlo: ZRODLO_COVERAGE,
    klasyfikacja: KLASYFIKACJA_DRIVE,
    swiezosc: SWIEZOSC_DRIVE,
    oknoBiezace: windowLabel,
    wyliczenie:
      'Rekordy dostaw mające OBA identyfikatory systemu głównego (szkoła i restauracja) ÷ rekordy dostaw w oknie.',
    szczegoly: [
      ['Rekordy dostaw', formatCount(pokrycie?.total ?? null)],
      ['Z identyfikatorem szkoły', formatCount(pokrycie?.with_school_tuszama_id ?? null)],
      ['Z identyfikatorem restauracji', formatCount(pokrycie?.with_restaurant_tuszama_id ?? null)],
      ['Złączalne', formatCount(pokrycie?.joinable ?? null)],
      ['Różnych szkół w rekordach', formatCount(pokrycie?.distinct_school_ids ?? null)],
    ],
    proba: `${formatCount(pokrycie?.total ?? null)} rekordów dostaw`,
    ograniczenia: coverage.data?.note ? [coverage.data.note] : [],
    czegoNieWynika:
      'To nie jest ocena punktualności ani jakości dostawy. Rekord bez identyfikatorów jest normalnie liczony w ' +
      'punktualności — nie wejdzie tylko do analizy łączonej ze sprzedażą. W drugą stronę: 100% nie znaczy, że ' +
      'złączenie jest POPRAWNE, tylko że jest możliwe.',
  };

  return (
    <Box>
      <NaglowekEkranu
        tytul="Punktualność dostaw"
        opis="Czy dostawy trafiają w godzinę, którą szkoła miała zapisaną w chwili dostawy — i ile z nich rozminęło się z nią w którąkolwiek stronę."
        akcja={<DateRangeFilter wariant="naglowek" comparison={range.comparison} />}
      />

      <PaskKlasyfikacjiZrodla />

      <Stack spacing={3} className="sygnal-kaskada">
        {/*
          TRZY DZIAŁY Z MAKIETY (`.dostawy-sekcja`): „Stan na teraz" → „Jak to się zmienia" →
          „Czego ten ekran nie wie". Produkt miał JEDEN dział, i to dopiero w połowie ekranu,
          więc pierwsza połowa czytała się jako ciąg równorzędnych kafelków.

          Trzeci dział jest tu najważniejszy i najłatwiej go pominąć: ograniczenia pomiaru
          stały dotąd na końcu jako dwie karty bez nazwy nadrzędnej, więc wyglądały na dalsze
          metryki, a nie na granice tego, co wolno z tego ekranu wyczytać.
        */}
        <PasSekcji wariant="cichy" tytul="Stan na teraz" />

        {/* Punktualność jest UDZIAŁEM, więc dostaje tarczę — tak jak w makiecie. Sama liczba
            „69,2%" nie mówi, ile brakuje do całości, a przy udziale to pierwsze pytanie.
            `wartosc` zostaje: niesie tekst dla czytnika ekranu i dla dowodu. */}
        <PlytaGlowna
          kartaId="DRIVE-03"
          dowodId="DRIVE-23"
          etykieta="Dostawy na czas"
          wartosc={naCzasPct === null ? null : formatPct(naCzasPct)}
          pierscien={{ wartosc: naCzasPct, etykieta: 'na czas' }}
          opis={
            <Typography variant="body2">
              {brakRekordow
                ? 'źródło nie zwróciło ani jednego rekordu dostawy w tym oknie — to nie jest zmierzona punktualność zero, tylko brak czego liczyć'
                : 'udział rekordów, które trafiły w godzinę zapisaną na rekordzie w chwili dostawy — przyjazd przed czasem i spóźnienie to dwa OSOBNE wyniki i żaden z nich nie jest trafieniem'}
            </Typography>
          }
          zrodlo={ZRODLO_SUMMARY}
          okno={`${windowLabel} · oś czasu: dzień dostawy`}
          dowod={dowodPunktualnosci}
          stopka={
            brakRekordow ? null : (
              <Typography variant="body2" data-cc-card-id="DRIVE-08" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                okno porównawcze {formatPct(previous.on_time_pct)} · zmiana{' '}
                <Box component="span" className="sygnal-liczba">
                  {formatPunkty(summary.data.on_time_delta_points)}
                </Box>
              </Typography>
            )
          }
          boczne={
            <>
              <MiniMetryka kartaId="DRIVE-04" wartosc={liczbaNaEkran(wyniki.naCzas)} etykieta="na czas" />
              <MiniMetryka kartaId="DRIVE-05" wartosc={liczbaNaEkran(wyniki.przedCzasem)} etykieta="przed czasem" />
              <MiniMetryka kartaId="DRIVE-06" wartosc={liczbaNaEkran(wyniki.spoznione)} etykieta="spóźnione" />
              <MiniMetryka kartaId="DRIVE-07" wartosc={liczbaNaEkran(current.deliveries)} etykieta="rekordów w oknie" />
            </>
          }
        />

        {brakRekordow ? (
          <Alert severity="warning" icon={false} data-cc-card-id="DRIVE-09">
            <AlertTitle>Brak dostaw w wybranym oknie</AlertTitle>
            <Typography variant="body2">
              Źródło nie zwróciło ani jednego rekordu dostawy dla okna {windowLabel}. To jest stan
              źródła, a nie wynik operacyjny: nie wiadomo, czy dowozów nie było, czy nie zostały
              zapisane. Zmień zakres dat albo sprawdź zasilanie systemu kierowców.
            </Typography>
            {rozjazdZiarna ? (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
                Uwaga na sprzeczność w źródle: podsumowanie mówi o zerze rekordów, a rozbicie na szkoły
                zwróciło {formatCount(szkolWWyniku)} szkół w tym samym oknie. Dwie odpowiedzi tego samego
                backendu nie zgadzają się ze sobą — to sygnał techniczny, nie wynik.
              </Typography>
            ) : null}
          </Alert>
        ) : (
          <PasekTrzechWynikow
            kartaId="DRIVE-10"
            wyniki={wyniki}
            podpis={
              <Typography variant="body2" data-cc-card-id="DRIVE-11" sx={{ color: 'var(--tekst-2)' }}>
                Spóźnienia w kubełkach: do 5 min {formatCount(current.late_5)} · do 15 min{' '}
                {formatCount(current.late_15)} · ponad 15 min {formatCount(current.late_over_15)}.
              </Typography>
            }
          />
        )}

        {!brakRekordow && current.without_snapshot > 0 ? (
          <Alert severity="warning" data-cc-card-id="DRIVE-12">
            {formatCount(current.without_snapshot)} z {formatCount(current.deliveries)} rekordów nie ma zapisanej
            planowanej godziny. Te rekordy nie są liczone jako punktualne — są liczone jako nieznane.
          </Alert>
        ) : null}

        {!brakRekordow && rozbieznoscKontroli ? (
          <Alert severity="warning" data-cc-card-id="DRIVE-13">
            <AlertTitle>Udział ze źródła nie zgadza się z jego własnymi licznikami</AlertTitle>
            <Typography variant="body2">
              Źródło podaje {formatPct(current.on_time_pct)}, a rekordy „na czas" podzielone przez wszystkie
              rekordy w oknie dają {formatPct(kontrolaPct)}. Na płycie pokazana jest liczba źródła. Różnica
              znaczy, że udział liczony jest na innym mianowniku — dopóki nie wiadomo na jakim, obie liczby
              opisują co innego.
            </Typography>
          </Alert>
        ) : null}

        <PasSekcji wariant="cichy" tytul="Jak to się zmienia" />

        {!brakRekordow && trend.data ? <PasekDni punkty={trend.data.series} zakres={trend.data.range ?? null} /> : null}

        <PasSekcji
          tytul="Czego nie widać w średniej"
          opis="Średnia punktualność potrafi wyglądać spokojnie, gdy kilka szkół ma naprawdę źle."
        />

        <Karta
          kartaId="DRIVE-17"
          nadtytul={
            trend.data ? (
              // Nadtytuł niesie WŁASNĄ wartość (kierunek serii ze źródła), więc dostaje własny
              // adres — inaczej byłby nie do odróżnienia od rankingu, w którego nagłówku siedzi.
              <Box component="span" data-cc-card-id="DRIVE-16">
                {`kierunek serii dziennej wg źródła: ${KIERUNEK_TRENDU[trend.data.direction] ?? trend.data.direction}`}
              </Box>
            ) : undefined
          }
          tytul="Szkoły wymagające uwagi"
          poziom="h3"
          akcja={
            <Button
              size="small"
              component={Link}
              to={`/drive/szkoly?from=${range.date_from}&to=${range.date_to}`}
            >
              pełne zestawienie szkół →
            </Button>
          }
        >

          {bySchool.isLoading ? <LoadingState label="Wczytywanie rozbicia na szkoły…" /> : null}
          {bySchool.error ? <ErrorState error={bySchool.error} /> : null}

          {!bySchool.isLoading && !bySchool.error ? (
            problems.length === 0 ? (
              /*
               * „Zero szkół z problemem" i „nie było czego oceniać" to dwa różne komunikaty.
               * Zielona plakietka przy pustym rozbiciu byłaby zapewnieniem wystawionym bez
               * pokrycia — czyli dokładnie tym, przed czym ma chronić rozróżnienie zero/pusto.
               */
              szkolWWyniku === null || szkolWWyniku === 0 ? (
                <Alert severity="warning" icon={false}>
                  Rozbicie na szkoły nie zwróciło w tym oknie ani jednej szkoły, więc nie ma czego oceniać.
                  To NIE znaczy, że żadna szkoła nie ma problemu — znaczy, że nie wiemy.
                </Alert>
              ) : (
                <Alert severity="success">
                  Żadna z {formatCount(szkolWWyniku)} szkół w wyniku nie schodzi poniżej {PROBLEM_THRESHOLD_PCT}%
                  punktualności przy co najmniej {MIN_DELIVERIES} dostawach. To jest wynik, nie brak danych —
                  szkoły z mniejszą próbą są z tej oceny wyłączone, bo przy niej wpadki nie da się odróżnić od
                  przypadku.
                </Alert>
              )
            ) : (
              <Stack spacing={1}>
                {problems.map((school) => {
                  const podzial = trojpodzial(school.current);
                  return (
                    <Paper key={school.school_id} variant="outlined" data-cc-card-id="DRIVE-18" sx={{ p: 2 }}>
                      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
                        <Typography variant="subtitle2" sx={{ minWidth: 220 }}>
                          {school.school_name ?? `Szkoła #${school.school_id}`}
                        </Typography>
                        <Typography variant="body2" className="sygnal-liczba">
                          na czas {formatPct(school.current.on_time_pct)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)' }} className="sygnal-liczba">
                          przed czasem {formatCount(podzial.przedCzasem)} · spóźnione {formatCount(podzial.spoznione)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)' }} className="sygnal-liczba">
                          zmiana {formatPunkty(school.on_time_delta_points)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)' }} className="sygnal-liczba">
                          {formatCount(school.current.deliveries)} dostaw
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <DataQualityBadge quality={school.data_quality} reason={school.data_quality_reason} />
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )
          ) : null}
        </Karta>

        <PasSekcji wariant="cichy" tytul="Czego ten ekran nie wie" />

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'stretch' }}>
          <KartaMetryki
            kartaId="DRIVE-20"
            etykieta="Średnie odchylenie"
            wartosc={brakRekordow ? null : formatMinuty(current.avg_delta_min)}
            zrodlo="Drive /analytics/delivery-summary → deliveries.delta_min"
            okno={`${windowLabel} · oś czasu: dzień dostawy`}
            dowod={dowodOdchylenia}
            jakosc={current.data_quality}
            powodJakosci={current.data_quality_reason}
          >
            <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
              mediana {formatMinuty(current.median_delta_min) ?? 'brak danych'} · zmiana{' '}
              {formatMinuty(summary.data.avg_delta_change_min) ?? 'brak danych'} · liczone tylko po rekordach z
              zapisanym odchyleniem
            </Typography>
          </KartaMetryki>

          <KartaMetryki
            kartaId="DRIVE-21"
            etykieta="Gotowe do łączenia ze sprzedażą"
            wartosc={pokrycie ? formatPct(pokrycie.joinable_pct) : null}
            zrodlo={ZRODLO_COVERAGE}
            okno={`${windowLabel} · oś czasu: dzień dostawy`}
            dowod={dowodZlaczenia}
          >
            <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
              {pokrycie
                ? `${formatCount(pokrycie.joinable)} z ${formatCount(pokrycie.total)} rekordów ma oba identyfikatory systemu głównego`
                : 'pokrycie identyfikatorów nie zostało jeszcze odczytane'}
            </Typography>
          </KartaMetryki>
        </Box>

        {summary.data.caveats.length > 0 ? (
          <Alert severity="info">
            <AlertTitle>Zastrzeżenia podane przez źródło</AlertTitle>
            <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0.25}>
              {summary.data.caveats.map((c) => (
                <Typography key={c} component="li" variant="body2">
                  {c}
                </Typography>
              ))}
            </Stack>
          </Alert>
        ) : null}
      </Stack>
    </Box>
  );
}
